import { LoadingOutlined } from '@ant-design/icons';
import { useApolloClient, useMutation } from '@apollo/client';
import { Button, message, Spin } from 'antd';
import { motion, useMotionValue } from 'framer-motion';
import { toPng } from 'html-to-image';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useWindowSize } from 'react-use';
import {
  ReactZoomPanPinchContentRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch';
import {
  CategoryEnum,
  EntranceDirectionEnum,
  S3PathEnum,
} from '../../__generated__/graphql';
import { AppContext } from '../../AppContext';
import {
  detectDeviceType,
  fileUpload,
  isTabletBrowser,
} from '../../common/utils';
import DirectionArrowSVG from '../../components/DirectionArrowSVG';
import useRouter from '../../hooks/useRouter';
import { AppContextType } from '../../types/appContext.type';
import {
  GENERATE_ANNOTATION_WEB,
  UPDATE_FLOOR_PLAN,
} from './graphql/mutations';

//Import css
import './styles/selectEntrance.less';

// Import images
import PlaceholderImage from '../../assets/images/dashboard-placeholder-image.png';
import Direction_arrow from '../../assets/images/direction-arrow.png';
import { ROUTES } from '../../common/constants';
import Sidebar from '../../components/Sidebar';
import SelectDirectionModal from './components/SelectDirectionModal';
import { GET_IMAGE_SIGNED_URL } from './graphql/queries';

type zoomProps = {
  positionX: number;
  positionY: number;
  scale: number;
};

type ImageConstraints = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

const directions: {
  type?: 'primary' | 'default' | 'link' | 'text' | 'dashed';
  direction: EntranceDirectionEnum;
  rotation: string;
  className?: string;
}[] = [
  {
    type: 'primary',
    direction: EntranceDirectionEnum.Left,
    rotation: '-90deg',
  },
  {
    type: 'default',
    direction: EntranceDirectionEnum.Top,
    rotation: '0deg',
    className: 'top',
  },
  {
    type: 'default',
    direction: EntranceDirectionEnum.Bottom,
    rotation: '-180deg',
  },
  {
    type: 'default',
    direction: EntranceDirectionEnum.Right,
    rotation: '90deg',
  },
];

const directionGroups = [
  [directions.find((d) => d.direction === EntranceDirectionEnum.Top)],
  [
    directions.find((d) => d.direction === EntranceDirectionEnum.Left),
    directions.find((d) => d.direction === EntranceDirectionEnum.Bottom),
    directions.find((d) => d.direction === EntranceDirectionEnum.Right),
  ],
];

const SelectEntrance = () => {
  const {
    state: { floorPlanSequence, setTourGuide },
  } = useContext(AppContext) as AppContextType;
  const [activeDirection, setActiveDirection] = useState<EntranceDirectionEnum>(
    EntranceDirectionEnum.Left,
  );
  const [rotate, setRotate] = useState<string>('-90deg');
  const [arrowPosition, setArrowPosition] = useState<{
    x: number;
    y: number;
  }>({ x: 50, y: 50 });
  const [isGuideModal, setIsGuideModal] = useState<boolean>(false);
  const [image, setImage] = useState<string>('');
  const [floorPlanId, setFloorPlanID] = useState<string>('');
  const [imageheight, setImageHeight] = useState<number>();
  const [imageWidth, setImageWidth] = useState<number>();
  const [imageZoomContainerHeight, setImageZoomContainerHeight] =
    useState<string>('');
  const [imageZoomContainerWidth, setImageZoomContainerWidth] =
    useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const { navigate } = useRouter();
  const client = useApolloClient();
  const { width } = useWindowSize();
  const [zoom, setzoom] = useState<zoomProps>({
    scale: 1,
    positionX: 0,
    positionY: 0,
  });

  const constraintsRef = useRef<HTMLDivElement>(null);
  const compassRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<ReactZoomPanPinchContentRef>(null);

  const constraintsRefDesktop = useRef<HTMLDivElement>(null);
  const compassRefDesktop = useRef<HTMLDivElement>(null);
  const arrowRefDesktop = useRef<HTMLDivElement>(null);
  const controlsRefDesktop = useRef<ReactZoomPanPinchContentRef>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [imageConstraints, setImageConstraints] =
    useState<ImageConstraints | null>(null);

  const [entranceUpdate] = useMutation(UPDATE_FLOOR_PLAN);

  useEffect(() => {
    if (setTourGuide) {
      setIsGuideModal(setTourGuide);
    }
    if (floorPlanSequence) {
      const baseImage =
        floorPlanSequence.assets?.find(
          (asset) => asset?.category === CategoryEnum.Base,
        )?.key || PlaceholderImage;
      setImage(baseImage);
      setFloorPlanID(floorPlanSequence.id || '');
      setImageHeight(floorPlanSequence.assets?.[0]?.height || 0);
      setImageWidth(floorPlanSequence.assets?.[0]?.width || 0);
    }
  }, [setTourGuide, floorPlanSequence]);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  useEffect(() => {
    const deviceType = detectDeviceType(width);

    let containerHeight = '';
    let containerWidth = '';

    switch (deviceType) {
      case 'extra-small':
        containerHeight = '400px';
        containerWidth = '300px';
        break;
      case 'small':
      case 'small-plus':
        containerHeight = '400px';
        containerWidth = '350px';
        break;
      case 'medium':
      case 'tablet-small':
        containerHeight = '400px';
        containerWidth = '500px';
        break;
      case 'tablet':
      case 'tablet-large':
        containerHeight = '500px';
        containerWidth = '700px';
        break;
      case 'desktop-small':
      case 'desktop-medium':
        containerHeight = '700px';
        containerWidth = '1024px';
        break;
      case 'desktop-large':
        containerHeight = '700px';
        containerWidth = '1200px';
        break;
      default:
        containerHeight = '';
        containerWidth = '';
    }

    const imageHeight = floorPlanSequence?.assets?.[0]?.height || 0;
    const imageWidth = floorPlanSequence?.assets?.[0]?.width || 0;

    if (
      imageHeight > parseInt(containerHeight) &&
      imageWidth > parseInt(containerWidth)
    ) {
      setImageZoomContainerHeight(containerHeight);
      setImageZoomContainerWidth(containerWidth);
    }
  }, [width, floorPlanSequence]);

  const handleClick = (direction: EntranceDirectionEnum) => {
    setActiveDirection(direction);
  };

  const buttonStyles = (direction: string) => ({
    backgroundColor:
      activeDirection === direction
        ? 'var(--primary-btn-bg)'
        : 'var(--secondary-btn-bg)',
    borderColor:
      activeDirection === direction
        ? 'var(--on-sc-variant)'
        : 'var(--on-sc-variant)',
  });

  const x = useMotionValue(50);
  const y = useMotionValue(50);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [constraints, setConstraints] = useState<any>(null);
  const [arrowDimensions, setArrowDimensions] = useState({
    width: 0,
    height: 0,
  });

  const calculateImageConstraints = useCallback(() => {
    if (isTabletBrowser(width)) return;

    const imageElement = imageRef.current;
    const containerElement = constraintsRefDesktop.current;
    const arrowElement = arrowRefDesktop.current;

    if (!imageElement || !containerElement || !arrowElement) return;

    const containerRect = containerElement.getBoundingClientRect();
    const imageRect = imageElement.getBoundingClientRect();
    const arrowRect = arrowElement.getBoundingClientRect();

    const imageLeft = imageRect.left - containerRect.left;
    const imageTop = imageRect.top - containerRect.top;
    const imageRight = imageLeft + imageRect.width - arrowRect.width;
    const imageBottom = imageTop + imageRect.height - arrowRect.height;

    setImageConstraints({
      left: imageLeft,
      top: imageTop,
      right: imageRight,
      bottom: imageBottom,
    });
  }, [width]);

  useEffect(() => {
    if (!isTabletBrowser(width) && zoom && imageWidth && imageheight) {
      calculateImageConstraints();
    }
  }, [zoom, imageWidth, imageheight, calculateImageConstraints, width]);
  const setInitialArrowPosition = useCallback(() => {
    if (isTabletBrowser(width)) {
      return;
    }

    const imageElement = imageRef.current;
    const containerElement = constraintsRefDesktop.current;

    if (!imageElement || !containerElement) return;

    const containerRect = containerElement.getBoundingClientRect();
    const imageRect = imageElement.getBoundingClientRect();

    const imageLeft = imageRect.left - containerRect.left;
    const imageTop = imageRect.top - containerRect.top;
    const imageCenterX = imageLeft + imageRect.width / 2;
    const imageCenterY = imageTop + imageRect.height / 2;

    setArrowPosition({
      x: imageCenterX - 15,
      y: imageCenterY - 15,
    });
  }, [width]);
  const [isCentered, setIsCentered] = useState(false);

  useEffect(() => {
    if (controlsRefDesktop.current && isImageLoaded) {
      const timer = setTimeout(() => {
        const { centerView } =
          controlsRefDesktop?.current as ReactZoomPanPinchContentRef;
        centerView(1.3, 200);

        setTimeout(() => {
          setIsCentered(true);
          calculateImageConstraints();
          setInitialArrowPosition();
        }, 300);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isImageLoaded, calculateImageConstraints, setInitialArrowPosition]);

  useEffect(() => {
    setIsCentered(false);
    setIsImageLoaded(false);
  }, [image]);
  useEffect(() => {
    const arrowElement = arrowRefDesktop.current || arrowRef.current;
    if (arrowElement) {
      const rect = arrowElement.getBoundingClientRect();
      setArrowDimensions({ width: rect.width, height: rect.height });
    }
  }, []);

  useEffect(() => {
    if (!zoom || !imageWidth || !imageheight || !arrowDimensions.width) {
      return;
    }

    const containerRef = isTabletBrowser(width)
      ? constraintsRef
      : constraintsRefDesktop;
    const container = containerRef.current;
    const imageElement = imageRef.current;

    if (!container || !imageElement) return;

    const containerRect = container.getBoundingClientRect();
    const imageRect = imageElement.getBoundingClientRect();

    const imageLeft = imageRect.left - containerRect.left;
    const imageTop = imageRect.top - containerRect.top;

    const newConstraints = {
      left: imageLeft,
      top: imageTop,
      right: imageLeft + imageRect.width - arrowDimensions.width,
      bottom: imageTop + imageRect.height - arrowDimensions.height,
    };

    setConstraints(newConstraints);

    const currentX = x.get();
    const currentY = y.get();

    const clampedX = Math.max(
      newConstraints.left,
      Math.min(currentX, newConstraints.right),
    );
    const clampedY = Math.max(
      newConstraints.top,
      Math.min(currentY, newConstraints.bottom),
    );

    if (clampedX !== currentX) {
      x.set(clampedX);
    }
    if (clampedY !== currentY) {
      y.set(clampedY);
    }
  }, [zoom, imageWidth, imageheight, arrowDimensions, x, y, width]);

  const handleGenerateReport = async () => {
    if (!compassRef.current || !compassRefDesktop.current) {
      message.error('Compass container is not available.');
      return;
    }
    if (floorPlanId?.length === 0) {
      message.error('Something went wrong, Please try again.');
      return;
    }
    try {
      setIsUploading(true);

      let relativeX = arrowPosition.x;
      let relativeY = arrowPosition.y;

      if (compassRef?.current || compassRefDesktop?.current) {
        if (!isTabletBrowser(width)) {
          const currentArrowElement = arrowRefDesktop.current;
          const currentImageElement = imageRef.current;

          if (currentArrowElement && currentImageElement && zoom) {
            const imageRect = currentImageElement.getBoundingClientRect();
            const arrowRect = currentArrowElement.getBoundingClientRect();
            relativeX = (arrowRect.left - imageRect.left) / zoom.scale;
            relativeY = (arrowRect.top - imageRect.top) / zoom.scale;
          }

          controlsRefDesktop?.current?.resetTransform();

          if (floorPlanSequence) {
            setImageZoomContainerHeight(
              `${floorPlanSequence?.assets?.[0]?.height}px`,
            );
            setImageZoomContainerWidth(
              `${floorPlanSequence?.assets?.[0]?.width}px`,
            );
          }

          await new Promise((resolve) => setTimeout(resolve, 300));

          if (controlsRefDesktop?.current) {
            controlsRefDesktop.current.setTransform(0, 0, 1, 0);
          }
          await new Promise((resolve) => setTimeout(resolve, 100));

          setArrowPosition({
            x: relativeX,
            y: relativeY,
          });

          await new Promise((resolve) => setTimeout(resolve, 200));
        } else {
          isTabletBrowser(width)
            ? controlsRef?.current?.resetTransform()
            : controlsRefDesktop?.current?.resetTransform();
          if (floorPlanSequence) {
            setImageZoomContainerHeight(
              `${floorPlanSequence?.assets?.[0]?.height}px`,
            );
            setImageZoomContainerWidth(
              `${floorPlanSequence?.assets?.[0]?.width}px`,
            );
          }
        }

        const node = isTabletBrowser(width)
          ? compassRef?.current
          : compassRefDesktop?.current;
        const parentNode = isTabletBrowser(width)
          ? constraintsRef?.current
          : constraintsRefDesktop?.current;

        const originalOverflow = node.style.overflow;

        const originalStyles = {
          height: node.style.height,
          width: node.style.width,
          overflow: node.style.overflow,
        };

        const originalParentStyles = parentNode
          ? {
              height: parentNode.style.height,
              width: parentNode.style.width,
              overflow: parentNode.style.overflow,
            }
          : null;

        node.style.height = 'auto';
        node.style.width = 'auto';
        node.style.overflow = 'visible';

        if (parentNode) {
          parentNode.style.height = 'auto';
          parentNode.style.width = 'auto';
          parentNode.style.overflow = 'visible';
        }
        if (
          (compassRef.current && arrowRef.current) ||
          (compassRefDesktop.current && arrowRefDesktop.current)
        ) {
          const sectionRect = isTabletBrowser(width)
            ? compassRef.current?.getBoundingClientRect()
            : compassRefDesktop.current?.getBoundingClientRect();
          const arrowRect = isTabletBrowser(width)
            ? arrowRef.current?.getBoundingClientRect()
            : arrowRefDesktop.current?.getBoundingClientRect();

          if (isTabletBrowser(width)) {
            const posX = zoom?.positionX ?? 0;
            const posY = zoom?.positionY ?? 0;
            const scale = zoom?.scale ?? 1;

            if (!sectionRect || !arrowRect) return;

            relativeX = (arrowRect.left - sectionRect.left - posX) / scale;
            relativeY = (arrowRect.top - sectionRect.top - posY) / scale;

            setArrowPosition({
              x: relativeX,
              y: relativeY,
            });
          }

          await new Promise((resolve) => setTimeout(resolve, 2500));

          const dataUrl = await toPng(node, {
            width: node?.scrollWidth,
            height: node?.scrollHeight,
          });

          node.style.overflow = originalOverflow;
          const blob = await (await fetch(dataUrl))?.blob();
          const file = new File([blob], 'entrance-screenshot.png', {
            type: 'image/png',
          });

          node.style.height = originalStyles.height;
          node.style.width = originalStyles.width;
          node.style.overflow = originalStyles.overflow;

          if (parentNode && originalParentStyles) {
            parentNode.style.height = originalParentStyles.height;
            parentNode.style.width = originalParentStyles.width;
            parentNode.style.overflow = originalParentStyles.overflow;
          }

          const deviceType = detectDeviceType(width);
          switch (deviceType) {
            case 'extra-small':
              setImageZoomContainerHeight('400px');
              setImageZoomContainerWidth('300px');
              break;
            case 'small':
            case 'small-plus':
              setImageZoomContainerHeight('400px');
              setImageZoomContainerWidth('350px');
              break;
            case 'medium':
            case 'tablet-small':
              setImageZoomContainerHeight('400px');
              setImageZoomContainerWidth('500px');
              break;
            case 'tablet':
            case 'tablet-large':
              setImageZoomContainerHeight('500px');
              setImageZoomContainerWidth('700px');
              break;
            case 'desktop-small':
            case 'desktop-medium':
              setImageZoomContainerHeight('700px');
              setImageZoomContainerWidth('1024px');
              break;
            case 'desktop-large':
              setImageZoomContainerHeight('700px');
              setImageZoomContainerWidth('1200px');
              break;
            default:
              setImageZoomContainerHeight('');
              setImageZoomContainerWidth('');
          }

          const signedUrlData = await client.query({
            query: GET_IMAGE_SIGNED_URL,
            variables: {
              data: {
                fileName: file.name,
                s3Path: S3PathEnum?.FloorPlanEntranceImage,
              },
            },
            fetchPolicy: 'network-only',
          });

          const signedUrl =
            signedUrlData?.data?.getImageSignedUrlWeb?.signedUrl;
          const signedUrlKey =
            signedUrlData?.data?.getImageSignedUrlWeb?.key || '';

          if (!signedUrl) {
            throw new Error(
              'Signed URL is missing or undefined. Cannot proceed.',
            );
          }
          await fileUpload(signedUrl, file);
          try {
            const { data } = await entranceUpdate({
              variables: {
                data: {
                  floorPlanId: floorPlanId,
                  entranceImage: signedUrlKey,
                  xAxis: Math.round(relativeX),
                  yAxis: Math.round(relativeY),
                  direction: activeDirection,
                },
              },
            });
            if (data?.updateFloorPlanWeb?.data) {
              const updatedFloorPlanId = data.updateFloorPlanWeb?.data?.id;

              const annotationProcessStart = await client.mutate({
                mutation: GENERATE_ANNOTATION_WEB,
                variables: {
                  input: {
                    floorPlanId: updatedFloorPlanId,
                  },
                },
                fetchPolicy: 'network-only',
              });

              const success =
                annotationProcessStart?.data?.generateAnnotationWeb?.status;

              if (success) {
                navigate(ROUTES.ANNOTATION_INFO);
              } else {
                const errorMsg =
                  annotationProcessStart?.errors
                    ?.map((err) => err.message)
                    .join(', ') || 'Annotation process failed.';
                message.error(errorMsg);
              }
            }
          } catch (error) {
            message.error('Submission failed. Please try again.');
          }
        }
      }
    } catch (error) {
      message.error('An error occurred. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleModalClose = () => {
    setIsGuideModal(false);
  };

  return (
    <>
      <div className={!isTabletBrowser(width) ? 'full-height' : ''}>
        <div className="entry-direction-container-desktop">
          <div
            className="floor-plan"
            style={{ display: !isTabletBrowser(width) ? 'flex' : 'none' }}
            ref={compassRefDesktop}
          >
            <div
              ref={constraintsRefDesktop}
              // need to give inline styling due to Runtime calculations based on image dimensions
              style={{
                width: imageZoomContainerWidth,
                height: imageZoomContainerHeight,
                overflow: 'auto',
              }}
              className="constraintref-div"
            >
              <TransformWrapper
                ref={controlsRefDesktop}
                onTransformed={(e) => {
                  setzoom(e.state);
                  if (!isTabletBrowser(width)) {
                    setTimeout(calculateImageConstraints, 50);
                  }
                }}
              >
                <TransformComponent>
                  <img
                    ref={imageRef}
                    onLoad={() => {
                      setIsImageLoaded(true);
                      if (!isTabletBrowser(width)) {
                        calculateImageConstraints();
                      }
                    }}
                    src={image}
                    style={{
                      width: imageWidth,
                      height: imageheight,
                      opacity: isImageLoaded ? 1 : 0,
                      transition: 'opacity 0.3s ease-in-out',
                    }}
                    alt="Floor Plan"
                    className="floor-plan-image"
                  />
                </TransformComponent>
              </TransformWrapper>

              {isImageLoaded && (
                <motion.div
                  ref={arrowRefDesktop}
                  drag={imageConstraints ? true : false}
                  dragMomentum={false}
                  dragElastic={0}
                  dragConstraints={imageConstraints as ImageConstraints}
                  animate={{ x: arrowPosition?.x, y: arrowPosition?.y }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    cursor: 'grab',
                    opacity: isImageLoaded && isCentered ? 1 : 0,
                    transition: 'opacity 0.3s ease-in-out',
                  }}
                  className="arrow-motion-div"
                >
                  <img
                    src={Direction_arrow}
                    alt="Direction Arrow"
                    className="direction-arrow-img"
                    draggable={false}
                    style={{ rotate: rotate }}
                  />
                </motion.div>
              )}
            </div>
          </div>
        </div>
        <Sidebar
          onSave={handleGenerateReport}
          headerContent={
            <>
              <div className="entry-direction-text-desktop body-base semibold">
                Drag and Drop on
                <span className=" body-base semibold">
                  your Property's main entrance door
                </span>
                on the image
              </div>
            </>
          }
          bodyContent={
            <div className="direction-choice-container-desktop">
              <div className="direction-choice-text body-base">
                Choose your <span>door's entry direction</span>
              </div>
              <div className="direction-choice-buttons">
                {directionGroups.map((group, rowIndex) => (
                  <div key={rowIndex} className="button-row">
                    {group.map(
                      (button) =>
                        button && (
                          <Button
                            key={button.direction}
                            type={button.type}
                            className={`direction-button ${button.className || ''}`}
                            onClick={() => {
                              handleClick(button.direction);
                              setRotate(button.rotation);
                            }}
                            style={buttonStyles(button.direction)}
                            icon={
                              <DirectionArrowSVG
                                direction={button.direction}
                                isActive={activeDirection === button.direction}
                              />
                            }
                          />
                        ),
                    )}
                  </div>
                ))}
              </div>
            </div>
          }
        ></Sidebar>
      </div>
      <div className="entry-direction-container">
        <SelectDirectionModal
          visible={isGuideModal}
          onClose={handleModalClose}
        ></SelectDirectionModal>
        {isUploading && (
          <div className="capture-image-loader">
            <Spin size="large" indicator={<LoadingOutlined spin />} />
          </div>
        )}
        <div
          className="entry-direction-text body-base semibold"
          style={{ display: isTabletBrowser(width) ? 'flex' : 'none' }}
        >
          Drag and Drop on <span> your Property's main entrance door </span> on
          the image
        </div>
        <div
          className="floor-plan"
          style={{ display: isTabletBrowser(width) ? 'flex' : 'none' }}
          ref={compassRef}
        >
          <div ref={constraintsRef} className="constraintref-div">
            <TransformWrapper
              ref={controlsRef}
              onTransformed={(e) => setzoom(e.state)}
            >
              <TransformComponent
                wrapperStyle={{
                  width: imageZoomContainerWidth,
                  height: imageZoomContainerHeight,
                }}
              >
                <img
                  src={image}
                  style={{ width: imageWidth, height: imageheight }}
                  alt="Floor Plan"
                  className="floor-plan-image"
                />
              </TransformComponent>
            </TransformWrapper>

            <motion.div
              drag
              dragMomentum={false}
              dragConstraints={constraints ? constraints : constraintsRef}
              animate={{ x: arrowPosition?.x, y: arrowPosition?.y }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                cursor: 'grab',
                x,
                y,
              }}
              className="arrow-motion-div"
              ref={arrowRef}
            >
              <img
                src={Direction_arrow}
                alt="Direction Arrow"
                className="direction-arrow-img"
                draggable={false}
                style={{ rotate: rotate }}
              />
            </motion.div>
          </div>
        </div>
        {isTabletBrowser(width) && (
          <div className="direction-choice-container">
            <div className="direction-choice-text body-base">
              Choose your <span>door's entry direction</span>
            </div>
            <div className="direction-choice-buttons">
              {directions.map(({ type, direction, rotation }) => (
                <Button
                  key={direction}
                  type={type}
                  className="direction-button"
                  onClick={() => {
                    handleClick(direction);
                    setRotate(rotation);
                  }}
                  style={buttonStyles(direction)}
                  icon={
                    <DirectionArrowSVG
                      direction={direction}
                      isActive={activeDirection === direction}
                    />
                  }
                />
              ))}
            </div>
          </div>
        )}
        {isTabletBrowser(width) && (
          <Button
            type="primary"
            htmlType="submit"
            className="generate-button"
            onClick={handleGenerateReport}
          >
            Generate Report
          </Button>
        )}
      </div>
    </>
  );
};

export default SelectEntrance;
